import type { IODADataPoint } from '../types/ioda';

/** Primary outage / chart threshold (merit-nt narrative). */
export const IDEAL_THRESHOLD = 35;

/** ≥10 min sustained at 5-min sampling steps ⇒ 3 consecutive samples below threshold. */
export const SUSTAINED_SAMPLE_COUNT = 3;

/** Prefer 35%; step down when no samples reach 35% in window. */
export const THRESHOLD_STEPS = [35, 30, 25, 20, 15, 10, 5, 2, 1, 0.5] as const;

export type ClockSource = 'merit' | 'ping';

export type OutageClockResolution =
  | {
      mode: 'active_outage';
      outageStartMs: number;
      threshold: number;
      source: ClockSource;
    }
  | {
      mode: 'recovery';
      lastGoodMs: number;
      threshold: number;
      source: ClockSource;
    }
  | {
      mode: 'span_below';
      firstSampleMs: number;
      maxPct: number;
    }
  | null;

function normalizeSeries(points: IODADataPoint[]): { from: number; value: number }[] {
  return points
    .filter((p) => p && Number.isFinite(p.from) && Number.isFinite(p.value))
    .map((p) => ({ from: p.from, value: Math.max(0, Math.min(100, p.value)) }))
    .sort((a, b) => a.from - b.from);
}

function findLastIndexAtOrAbove(sorted: { from: number; value: number }[], t: number): number | null {
  let idx: number | null = null;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].value >= t) idx = i;
  }
  return idx;
}

function sustainedBelowFrom(
  sorted: { from: number; value: number }[],
  startIdx: number,
  threshold: number,
  need: number
): boolean {
  if (startIdx + need > sorted.length) return false;
  for (let j = 0; j < need; j++) {
    if (sorted[startIdx + j].value >= threshold) return false;
  }
  return true;
}

function debouncedOutageStartAfter(
  sorted: { from: number; value: number }[],
  lastGoodIdx: number | null,
  threshold: number
): number | null {
  const scanFrom = lastGoodIdx == null ? 0 : lastGoodIdx + 1;
  for (let i = scanFrom; i <= sorted.length - SUSTAINED_SAMPLE_COUNT; i++) {
    if (sustainedBelowFrom(sorted, i, threshold, SUSTAINED_SAMPLE_COUNT)) {
      return i;
    }
  }
  return null;
}

function windowSpanInfo(points: IODADataPoint[]): { firstMs: number; maxPct: number } | null {
  const s = normalizeSeries(points);
  if (s.length === 0) return null;
  const maxPct = Math.max(...s.map((p) => p.value));
  return { firstMs: s[0].from * 1000, maxPct };
}

function trySeries(
  sorted: { from: number; value: number }[],
  currentPct: number,
  source: ClockSource
): OutageClockResolution | null {
  for (const thr of THRESHOLD_STEPS) {
    const lastGoodIdx = findLastIndexAtOrAbove(sorted, thr);
    if (lastGoodIdx == null) continue;

    const lastGoodMs = sorted[lastGoodIdx].from * 1000;

    if (currentPct >= IDEAL_THRESHOLD) {
      return { mode: 'recovery', lastGoodMs, threshold: thr, source };
    }

    let startIdx = debouncedOutageStartAfter(sorted, lastGoodIdx, thr);
    if (startIdx == null && lastGoodIdx + 1 < sorted.length) {
      startIdx = lastGoodIdx + 1;
    }
    const outageStartMs = startIdx != null ? sorted[startIdx].from * 1000 : lastGoodMs;
    return { mode: 'active_outage', outageStartMs, threshold: thr, source };
  }
  return null;
}

export function resolveOutageClock(
  meritPoints: IODADataPoint[],
  pingPoints: IODADataPoint[] | undefined,
  currentPct: number
): OutageClockResolution {
  const merit = normalizeSeries(meritPoints);
  if (merit.length === 0) return null;

  const fromMerit = trySeries(merit, currentPct, 'merit');
  if (fromMerit) return fromMerit;

  if (pingPoints?.length) {
    const ping = normalizeSeries(pingPoints);
    const fromPing = trySeries(ping, currentPct, 'ping');
    if (fromPing) return fromPing;
  }

  const span = windowSpanInfo(meritPoints);
  if (span) {
    const s = normalizeSeries(meritPoints);
    const startIdx = debouncedOutageStartAfter(s, null, THRESHOLD_STEPS[THRESHOLD_STEPS.length - 1]);
    const firstMs = startIdx != null ? s[startIdx].from * 1000 : span.firstMs;
    return { mode: 'span_below', firstSampleMs: firstMs, maxPct: span.maxPct };
  }

  return null;
}

/** English: "Feb 28, 2026, 7:10 AM UTC" — citation line for journalists. */
export function formatCountedFromUtc(ms: number, useFa: boolean): string {
  const d = new Date(ms);
  if (useFa) {
    return (
      d.toLocaleString('fa-IR', {
        dateStyle: 'full',
        timeStyle: 'short',
        timeZone: 'UTC',
      }) + ' UTC'
    );
  }
  return (
    d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    }) + ' UTC'
  );
}

/** Visual states: A &lt;35%, B 35–60%, C &gt;60%. */
export function clockStateClass(currentPct: number): 'clock-state-active' | 'clock-state-recovery' | 'clock-state-normal' {
  if (currentPct < 35) return 'clock-state-active';
  if (currentPct <= 60) return 'clock-state-recovery';
  return 'clock-state-normal';
}

export function digitColorClass(currentPct: number): string {
  if (currentPct < 35) return 'text-ir-red';
  if (currentPct <= 60) return 'text-ir-amber';
  return 'text-ir-muted';
}
